import React, { useState, useRef } from 'react';
import { Modal, Card, Tag, Image, Button, Upload, DatePicker, message } from 'antd';
import { EyeOutlined, UploadOutlined, CameraOutlined, EditOutlined, SaveOutlined, CheckCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import { ITicket, TicketStatus, getTicketStatusText, getTicketStatusColor, formatCurrency, TransactionActionType, getTransactionActionText, getTransactionActionColor } from '../ticketModels';
import { $api } from 'services/service';
import browserPrinterService from 'services/browserPrinter.service';
import moment from 'moment';
import 'moment/locale/vi';
import locale from 'antd/es/date-picker/locale/vi_VN';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';

interface TicketDetailModalProps {
    visible: boolean;
    ticket: ITicket | null;
    onClose: () => void;
    onTicketUpdate?: (ticket: ITicket) => void;
    showUseButton?: boolean;
    onUseTicket?: (ticket: ITicket) => void;
}

const TicketDetailModal: React.FC<TicketDetailModalProps> = ({
    visible,
    ticket,
    onClose,
    onTicketUpdate,
    showUseButton = false,
    onUseTicket,
}) => {
    const [isEditingExpireDate, setIsEditingExpireDate] = useState(false);
    const [newExpireDate, setNewExpireDate] = useState<moment.Moment | null>(null);
    const [savingExpireDate, setSavingExpireDate] = useState(false);
    const [uploadingPic, setUploadingPic] = useState(false);
    const [cameraModalVisible, setCameraModalVisible] = useState(false);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [localTicket, setLocalTicket] = useState<ITicket | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    React.useEffect(() => {
        if (ticket) {
            setLocalTicket(ticket);
        }
    }, [ticket]);

    const handleEditExpireDate = () => {
        if (localTicket) {
            setIsEditingExpireDate(true);
            setNewExpireDate(moment(localTicket.expireDate));
        }
    };

    const handleCancelEditExpireDate = () => {
        setIsEditingExpireDate(false);
        setNewExpireDate(null);
    };

    const handleSaveExpireDate = async () => {
        if (!localTicket || !newExpireDate) {
            message.warning('Vui lòng chọn ngày hết hạn');
            return;
        }

        setSavingExpireDate(true);
        try {
            const res: any = await $api.ticket.update({
                ticketId: localTicket.id,
                newExpireDate: newExpireDate.toISOString()
            });

            if (res.result) {
                message.success('Cập nhật ngày hết hạn thành công!');
                const updatedTicket = {
                    ...localTicket,
                    expireDate: newExpireDate.toISOString()
                };
                setLocalTicket(updatedTicket);
                setIsEditingExpireDate(false);
                setNewExpireDate(null);
                onTicketUpdate?.(updatedTicket);
            }
        } catch (e: any) {
            let errMsg;
            let type = typeof e?.response?.data?.errors?.exceptionMessage;
            if (type === 'string') {
                errMsg = e?.response?.data?.errors?.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e?.response?.data?.errors?.exceptionMessage?.title;
            } else {
                errMsg = 'Có lỗi xảy ra khi cập nhật ngày hết hạn';
            }
            message.error(errMsg);
        } finally {
            setSavingExpireDate(false);
        }
    };

    const handleUploadProfilePic = async (file: File) => {
        if (!localTicket) return false;

        setUploadingPic(true);
        try {
            const formData = new FormData();
            formData.append('formFile', file);

            const res: any = await $api.student.uploadAvatar(formData, localTicket.studentId);
            if (res.result != null) {
                message.success('Upload ảnh đại diện thành công!');
                const newImageUrl = `${res.result}?t=${Date.now()}`;
                const updatedTicket = {
                    ...localTicket,
                    profilePicUrl: newImageUrl
                };
                setLocalTicket(updatedTicket);
                onTicketUpdate?.(updatedTicket);
            }
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Có lỗi xảy ra khi upload ảnh');
        } finally {
            setUploadingPic(false);
        }
        return false;
    };

    const handleOpenCamera = async () => {
        setCameraModalVisible(true);
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'user'
                }
            });
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        } catch (error) {
            message.error('Không thể truy cập camera. Vui lòng kiểm tra quyền truy cập.');
            setCameraModalVisible(false);
        }
    };

    const handleCloseCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        setCameraModalVisible(false);
    };

    const handleCapture = async () => {
        if (!videoRef.current || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const video = videoRef.current;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const context = canvas.getContext('2d');
        if (context) {
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(async (blob) => {
                if (blob) {
                    const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
                    handleCloseCamera();
                    await handleUploadProfilePic(file);
                }
            }, 'image/jpeg', 0.95);
        }
    };

    const canUseTicket = (ticket: ITicket): boolean => {
        return ticket.status === TicketStatus.Active;
    };

    const handleModalClose = () => {
        setIsEditingExpireDate(false);
        setNewExpireDate(null);
        onClose();
    };

    const handlePrintTicket = async () => {
        if (!localTicket) return;

        try {
            // Chuẩn bị dữ liệu in
            browserPrinterService.printTicket({
                id: localTicket.id,
                code: localTicket.code,
                studentName: localTicket.studentName,
                studentUserInternalId: localTicket.studentUserInternalId,
                ticketTypeName: localTicket.ticketTypeName,
                ticketTypeCode: localTicket.ticketTypeCode || '',
                price: localTicket.price,
                createdByName: localTicket.createdByName,
                creationDate: localTicket.creationDate || new Date().toISOString(),
                expireDate: localTicket.expireDate,
                qrCode: localTicket.qrCode,
                isUsed: localTicket.status === TicketStatus.Used ? true : undefined,
                note: localTicket.note
            });

            message.success('Đã mở cửa sổ in!');
        } catch (error: any) {
            console.error('Error printing ticket:', error);
            message.error(error?.message || 'Có lỗi xảy ra khi in phiếu.');
        }
    };

    return (
        <>
            <Modal
                title={
                    <div className="flex items-center gap-2">
                        <EyeOutlined className="text-blue-600" />
                        <span className="text-xl font-bold">Chi tiết phiếu</span>
                    </div>
                }
                open={visible}
                onCancel={handleModalClose}
                footer={[
                    <Button key="close" onClick={handleModalClose}>
                        Đóng
                    </Button>
                ]}
                width="80%"
                style={{
                    top: 20,
                    maxWidth: 800
                }}
                className="ticket-detail-modal"
            >
                {localTicket && (
                    <div className="space-y-6">
                        {/* QR Code and Profile Picture Section */}
                        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                            {/* QR Code Section */}
                            <div className="text-center bg-white p-4 rounded-lg border-2 border-gray-200">
                                {localTicket.qrCode ? (
                                    <>
                                        <Image
                                            src={`${localTicket.qrCode}`}
                                            alt="QR Code"
                                            width={200}
                                            className="mx-auto"
                                        />
                                        <p className="text-gray-500 text-sm mt-2">Quét mã QR để sử dụng phiếu</p>
                                    </>
                                ) : (
                                    <div className="flex flex-col items-center justify-center" style={{ minHeight: 200 }}>
                                        <p className="text-gray-400 text-sm">Chưa có mã QR</p>
                                    </div>
                                )}
                            </div>

                            {/* Profile Picture Section */}
                            <div className="text-center bg-white p-4 rounded-lg border-2 border-gray-200">
                                {localTicket.profilePicUrl ? (
                                    <>
                                        <Image
                                            src={`${localTicket.profilePicUrl}`}
                                            alt="Ảnh đại diện"
                                            width={200}
                                            height={200}
                                            className="mx-auto rounded-lg object-cover"
                                            style={{ width: 200, height: 200 }}
                                        />
                                        <p className="text-gray-500 text-sm mt-2 mb-3">Ảnh đại diện sinh viên</p>
                                        <div className="flex gap-2 justify-center flex-wrap">
                                            <Upload
                                                accept="image/*"
                                                showUploadList={false}
                                                beforeUpload={handleUploadProfilePic}
                                                disabled={uploadingPic}
                                            >
                                                <Button
                                                    icon={<UploadOutlined />}
                                                    loading={uploadingPic}
                                                    size="small"
                                                    type="dashed"
                                                >
                                                    {uploadingPic ? 'Đang upload...' : 'Upload lại'}
                                                </Button>
                                            </Upload>
                                            <Button
                                                icon={<CameraOutlined />}
                                                size="small"
                                                type="dashed"
                                                onClick={handleOpenCamera}
                                                disabled={uploadingPic}
                                            >
                                                Chụp lại
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex flex-col items-center justify-center" style={{ minHeight: 200 }}>
                                        <div className="flex gap-2 mb-3 flex-wrap justify-center">
                                            <Upload
                                                accept="image/*"
                                                showUploadList={false}
                                                beforeUpload={handleUploadProfilePic}
                                                disabled={uploadingPic}
                                            >
                                                <Button
                                                    icon={<UploadOutlined />}
                                                    loading={uploadingPic}
                                                    size="large"
                                                    type="dashed"
                                                >
                                                    {uploadingPic ? 'Đang upload...' : 'Upload ảnh'}
                                                </Button>
                                            </Upload>
                                            <Button
                                                icon={<CameraOutlined />}
                                                size="large"
                                                type="dashed"
                                                onClick={handleOpenCamera}
                                                disabled={uploadingPic}
                                            >
                                                Chụp ảnh
                                            </Button>
                                        </div>
                                        <p className="text-gray-400 text-xs mt-2">Chưa có ảnh đại diện</p>
                                        <p className="text-gray-400 text-xs">Click để upload hoặc chụp ảnh</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Use Ticket Button - Large and Prominent */}
                        {showUseButton && localTicket && canUseTicket(localTicket) && (
                            <div className="flex justify-center">
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => onUseTicket?.(localTicket)}
                                    className="w-full md:w-auto px-8 md:px-16 h-12 md:h-16 text-xl md:text-2xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                                >
                                    Sử dụng phiếu
                                </Button>
                            </div>
                        )}

                        {/* Ticket Info Card */}
                        <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200">
                            <div className="text-center mb-4">
                                <p className="text-gray-600 text-sm mb-2">Mã phiếu</p>
                                <p className="font-mono font-bold text-2xl md:text-3xl text-blue-600 mb-3 break-all">{localTicket.code}</p>
                                <Tag color={getTicketStatusColor(localTicket.status)} className="text-base md:text-lg px-3 md:px-4 py-1">
                                    {getTicketStatusText(localTicket.status)}
                                </Tag>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                <div className="bg-white p-3 rounded-lg">
                                    <p className="text-gray-500 text-sm mb-1">👤 Sinh viên</p>
                                    <p className="font-bold text-base md:text-lg break-words">{localTicket.studentName}</p>
                                    <p className="text-gray-600 text-sm">{localTicket.studentUserInternalId}</p>
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                    <p className="text-gray-500 text-sm mb-1">🎫 Loại phiếu</p>
                                    <p className="font-bold text-base md:text-lg break-words">{localTicket.ticketTypeName}</p>
                                    {localTicket.ticketTypeCode && (
                                        <p className="text-gray-600 text-sm font-mono">{localTicket.ticketTypeCode}</p>
                                    )}
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                    <p className="text-gray-500 text-sm mb-1">💰 Giá phiếu</p>
                                    <p className="font-bold text-xl md:text-2xl text-green-600">{formatCurrency(localTicket.price)}</p>
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                    <p className="text-gray-500 text-sm mb-1">⏰ Thời gian tạo</p>
                                    <p className="font-semibold text-sm md:text-base">{moment(localTicket.creationDate).format('HH:mm DD/MM/YYYY')}</p>
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                    <p className="text-gray-500 text-sm mb-1">📅 Ngày hết hạn</p>
                                    {isEditingExpireDate ? (
                                        <div className="space-y-2">
                                            <DatePicker
                                                showTime
                                                format="DD/MM/YYYY HH:mm"
                                                value={newExpireDate}
                                                onChange={(date) => setNewExpireDate(date)}
                                                className="w-full"
                                                placeholder="Chọn ngày hết hạn"
                                                locale={locale}
                                                defaultPickerValue={dayjs(moment(localTicket.expireDate).format('YYYY-MM-DD HH:mm'))}
                                            />
                                            <div className="flex gap-2">
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<SaveOutlined />}
                                                    onClick={handleSaveExpireDate}
                                                    loading={savingExpireDate}
                                                    className="flex-1"
                                                >
                                                    Lưu
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={handleCancelEditExpireDate}
                                                    disabled={savingExpireDate}
                                                    className="flex-1"
                                                >
                                                    Hủy
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between gap-2">
                                            <p className={`font-semibold text-sm md:text-base ${moment(localTicket.expireDate).isBefore(moment()) ? 'text-red-600' : 'text-green-600'}`}>
                                                {moment(localTicket.expireDate).format('HH:mm DD/MM/YYYY')}
                                            </p>
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={handleEditExpireDate}
                                                className="p-0 h-auto"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="bg-white p-3 rounded-lg">
                                    <p className="text-gray-500 text-sm mb-1">👨‍💼 Người bán</p>
                                    <p className="font-semibold text-sm md:text-base break-words">{localTicket.createdByName}</p>
                                </div>
                            </div>

                            {localTicket.note && (
                                <div className="bg-white p-3 rounded-lg mt-4">
                                    <p className="text-gray-500 text-sm mb-2">📝 Ghi chú</p>
                                    <p className="text-gray-800 break-words">{localTicket.note}</p>
                                </div>
                            )}
                        </Card>

                        {/* Transaction History */}
                        {localTicket.transactions && localTicket.transactions.length > 0 && (
                            <div>
                                <h3 className="text-base md:text-lg font-bold mb-3 flex items-center gap-2">
                                    <span className="text-blue-600">📋</span>
                                    Lịch sử giao dịch
                                </h3>
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                    {localTicket.transactions.map((transaction: any) => (
                                        <div
                                            key={transaction.id}
                                            className="bg-gray-50 border-l-4 border-blue-500 p-3 md:p-4 rounded-r-lg hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="text-xs md:text-sm text-gray-500 mb-2">
                                                <span className="font-medium">⏰ {moment(transaction.actionDate).format('HH:mm DD/MM/YYYY')}</span>
                                            </div>

                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <span className="font-semibold text-gray-800 text-sm md:text-base break-words">{transaction.detail}</span>
                                                        {transaction.transactionType === 1 && (
                                                            <Tag color="green">Bán phiếu</Tag>
                                                        )}
                                                        {transaction.transactionType === 2 && (
                                                            <Tag color="blue">Sử dụng</Tag>
                                                        )}
                                                        {transaction.transactionType === 3 && (
                                                            <Tag color="red">Hủy</Tag>
                                                        )}
                                                        {transaction.transactionType === 5 && (
                                                            <Tag color="orange">Kéo dài</Tag>
                                                        )}
                                                        {transaction.transactionType === 6 && (
                                                            <Tag color="purple">Tặng phiếu</Tag>
                                                        )}
                                                    </div>
                                                    {transaction.note && (
                                                        <p className="text-gray-600 text-xs md:text-sm mt-1 break-words">
                                                            <span className="font-medium">Ghi chú:</span> {transaction.note}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {transaction.actionByName && (
                                                <div className="text-xs md:text-sm text-gray-500 mt-2">
                                                    <span className="font-medium">Người thực hiện:</span>{' '}
                                                    {transaction.actionByName}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(!localTicket.transactions || localTicket.transactions.length === 0) && (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <p className="text-gray-500">Chưa có lịch sử giao dịch</p>
                            </div>
                        )}

                        {/* Print Ticket Button */}
                        <div className="flex justify-center">
                            <Button
                                type="default"
                                size="middle"
                                icon={<PrinterOutlined />}
                                onClick={handlePrintTicket}
                                className="w-full md:w-auto px-8 md:px-16 h-12 md:h-16 text-xl md:text-2xl font-bold shadow-lg hover:shadow-xl
                                transform hover:scale-105 transition-all focus:outline-none focus:shadow-none active:shadow-none"
                            >
                                In vé
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Camera Modal */}
            <Modal
                title="Chụp ảnh đại diện"
                open={cameraModalVisible}
                onCancel={handleCloseCamera}
                footer={null}
                width="95%"
                style={{ maxWidth: 600 }}
                centered
            >
                <div className="flex flex-col items-center">
                    <video
                        ref={videoRef}
                        autoPlay
                        className="w-full rounded-lg shadow-md"
                    />
                    <canvas
                        ref={canvasRef}
                        style={{ display: 'none' }}
                    />
                    <div className="flex gap-4 mt-4">
                        <Button
                            type="primary"
                            size="large"
                            onClick={handleCapture}
                            icon={<CameraOutlined />}
                            className="flex-1"
                        >
                            Chụp ảnh
                        </Button>
                        <Button
                            size="large"
                            onClick={handleCloseCamera}
                            className="flex-1"
                        >
                            Hủy
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default TicketDetailModal;
